import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Mic, MicOff, Volume2, VolumeX,
  Send, MessageSquare, Plus, Loader2, LogIn, Cpu,
  Copy, Check, PanelLeftOpen, X, Sparkles, Clock, Lock
} from "lucide-react";
import api from "../lib/api";
import { MODEL_BADGE_COLORS, normalizeModelId, getModelName } from "../lib/aiModels";

/* ─── Helpers ─── */
function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatMsgTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* ─── Simple Markdown Renderer ─── */
function processInline(text) {
  const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*") && !p.startsWith("**")) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={i} className="bg-muted/70 px-1 py-0.5 rounded text-[11px] font-mono">{p.slice(1, -1)}</code>;
    return p;
  });
}

function MarkdownContent({ text }) {
  if (!text) return null;
  const blocks = text.split(/(```[\s\S]*?```)/g);
  return (
    <div className="text-sm leading-relaxed">
      {blocks.map((block, bi) => {
        if (block.startsWith("```")) {
          const code = block.slice(3, -3).replace(/^\w*\n/, "");
          return <pre key={bi} className="bg-gray-800 text-foreground rounded-lg p-3 my-2 overflow-x-auto text-xs font-mono"><code>{code}</code></pre>;
        }
        const lines = block.split("\n");
        return lines.map((line, li) => {
          const key = `${bi}-${li}`;
          if (line.match(/^[-*] /)) return <li key={key} className="ml-4 list-disc text-sm">{processInline(line.replace(/^[-*] /, ""))}</li>;
          if (line.match(/^\d+\. /)) return <li key={key} className="ml-4 list-decimal text-sm">{processInline(line.replace(/^\d+\. /, ""))}</li>;
          if (line.trim() === "") return null;
          return <p key={key} className="mb-1 last:mb-0">{processInline(line)}</p>;
        });
      })}
    </div>
  );
}

/* ─── Message Bubble ─── */
function MessageBubble({ msg, aiName, onCopy, copiedId }) {
  const isUser = msg.role === "user";
  const initials = aiName ? aiName.charAt(0).toUpperCase() : "A";

  return (
    <div className={`flex gap-3 animate-message-in ${isUser ? "flex-row-reverse" : ""}`} data-testid={`msg-${msg.role}-${msg.id || ""}`}>
      {/* Avatar */}
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-xs font-medium mt-1">
          {initials}
        </div>
      )}
      <div className={`max-w-[75%] min-w-0 ${isUser ? "items-end" : "items-start"}`}>
        {/* Name + Time */}
        <div className={`flex items-center gap-2 mb-1 ${isUser ? "justify-end" : ""}`}>
          <span className={`text-[11px] font-medium ${isUser ? "text-muted-foreground" : "text-cyan-400"}`}>
            {isUser ? "You" : aiName}
          </span>
          <span className="text-[10px] text-muted-foreground/70">{formatMsgTime(msg.created_at)}</span>
        </div>
        {/* Bubble */}
        <div className={`rounded-2xl px-4 py-3 ${
          isUser
            ? "bg-gradient-to-r from-cyan-400 to-violet-500 text-white rounded-br-md shadow-glow-sm"
            : "glass-panel text-foreground/80 rounded-bl-md"
        }`}>
          {isUser ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <>
              <MarkdownContent text={msg.content} />
              {msg.streaming && <span className="inline-block w-0.5 h-4 bg-cyan-400 ml-0.5 animate-pulse" />}
            </>
          )}
        </div>
        {/* Footer */}
        <div className={`flex items-center gap-2 mt-1 ${isUser ? "justify-end" : ""}`}>
          {isUser && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
              Delivered <Check className="w-3 h-3" />
            </span>
          )}
          {!isUser && (
            <button
              onClick={() => onCopy(msg)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground/70 hover:text-cyan-400 bg-transparent border-0 cursor-pointer transition-colors"
              data-testid={`copy-msg-${msg.id || ""}`}
            >
              {copiedId === msg.id ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Conversation Sidebar Item ─── */
function ConvItem({ conv, active, onClick, aiName }) {
  const firstUser = conv.messages?.find((m) => m.role === "user");
  const preview = firstUser?.content?.slice(0, 60) || "New conversation";
  const count = conv.messages?.length || 0;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 rounded-xl transition-all border-0 cursor-pointer mb-1 ${
        active ? "bg-cyan-500/10" : "bg-transparent hover:bg-white/5"
      }`}
      data-testid={`conv-item-${conv.id}`}
    >
      <div className="flex items-center justify-between mb-0.5">
        <span className={`text-xs font-medium truncate ${active ? "text-cyan-400" : "text-foreground/80"}`}>{aiName}</span>
        <span className="text-[10px] text-muted-foreground/70 flex-shrink-0 ml-2">{formatRelativeTime(conv.updated_at)}</span>
      </div>
      <p className="text-[11px] text-muted-foreground truncate mb-0.5">{preview}</p>
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
        <MessageSquare className="w-2.5 h-2.5" /> {count}
      </div>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════
   Main Chat Page
   ═══════════════════════════════════════════════════════ */
export default function ChatPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  /* ─── Core State ─── */
  const [assistant, setAssistant] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ─── Conversations ─── */
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);

  /* ─── Input ─── */
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);

  /* ─── Voice ─── */
  const [isListening, setIsListening] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  /* ─── UI ─── */
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  /* ─── Speech Support Check ─── */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SR);
    synthRef.current = window.speechSynthesis;
  }, []);

  /* ─── Cleanup ─── */
  useEffect(() => {
    return () => {
      if (recognitionRef.current) { recognitionRef.current.abort(); recognitionRef.current = null; }
      if (synthRef.current) { synthRef.current.cancel(); }
    };
  }, []);

  /* ─── Fetch Assistant ─── */
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/assistants/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setAssistant(r.data);
        if (user && r.data.user_id === user.id) {
          setIsOwner(true);
          loadConversations(slug);
        }
      })
      .catch(() => { if (!cancelled) setError("Assistant not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, user]);

  const loadConversations = async (s) => {
    try {
      const r = await api.get(`/chat/${s}/history`);
      setConversations(r.data);
      if (r.data.length > 0) {
        setActiveConvId(r.data[0].id);
        setMessages(r.data[0].messages || []);
      }
    } catch { /* no history */ }
  };

  /* ─── Auto-scroll ─── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, liveTranscript]);

  /* ─── Select Conversation ─── */
  const selectConversation = (convId) => {
    const conv = conversations.find((c) => c.id === convId);
    setActiveConvId(convId);
    setMessages(conv?.messages || []);
    setSidebarOpen(false);
  };

  const newConversation = () => {
    setActiveConvId(null);
    setMessages([]);
    setLiveTranscript("");
    setSidebarOpen(false);
    inputRef.current?.focus();
  };

  /* ─── Send Message ─── */
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || sending) return;
    const now = new Date().toISOString();
    const userMsg = { role: "user", content: text.trim(), id: `temp-${Date.now()}`, created_at: now };
    setMessages((prev) => [...prev, userMsg]);
    setInputText("");
    setLiveTranscript("");
    setSending(true);

    // Add placeholder AI message for streaming
    const aiMsgId = `stream-${Date.now()}`;
    setMessages((prev) => [...prev, { role: "assistant", content: "", id: aiMsgId, created_at: new Date().toISOString(), streaming: true }]);

    try {
      // Token is stored under "appmi_token" by AuthContext — using "token"
      // here meant streaming chat always sent ``Authorization: Bearer null``.
      const token = localStorage.getItem("appmi_token");
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      const params = new URLSearchParams({ message: text.trim() });
      if (activeConvId) params.set("conversation_id", activeConvId);

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/chat/${slug}/stream?${params}`, {
        headers,
        credentials: "include",
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let finalConvId = activeConvId;
      let finalMsgId = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.done) {
                finalConvId = data.conversation_id || finalConvId;
                finalMsgId = data.message_id || "";
              } else {
                fullContent += data.content;
                setMessages((prev) => {
                  const updated = [...prev];
                  const idx = updated.findIndex((m) => m.id === aiMsgId);
                  if (idx >= 0) updated[idx] = { ...updated[idx], content: fullContent };
                  return updated;
                });
              }
            } catch {}
          }
        }
      }

      // Finalize: remove streaming flag, set final IDs
      setMessages((prev) => prev.map((m) => m.id === aiMsgId ? { ...m, id: finalMsgId || aiMsgId, streaming: false } : m));

      // Update conversations
      if (finalConvId) {
        setConversations((prev) => {
          const exists = prev.find((c) => c.id === finalConvId);
          if (exists) return prev.map((c) => c.id === finalConvId ? { ...c, updated_at: new Date().toISOString() } : c);
          return [{ id: finalConvId, created_at: now, updated_at: new Date().toISOString() }, ...prev];
        });
        setActiveConvId(finalConvId);
      }

      // TTS after streaming complete
      if (ttsEnabled && synthRef.current && fullContent) speakText(fullContent);
    } catch {
      setMessages((prev) => prev.map((m) => m.id === aiMsgId ? { ...m, content: "I'm having trouble responding right now. Please try again.", streaming: false } : m));
    } finally {
      setSending(false);
    }
  }, [slug, activeConvId, sending, ttsEnabled]);

  /* ─── Speech Recognition ─── */
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (recognitionRef.current) recognitionRef.current.abort();

    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => { setIsListening(true); setLiveTranscript(""); };
    recognition.onresult = (event) => {
      let t = "";
      for (let i = 0; i < event.results.length; i++) t += event.results[i][0].transcript;
      setLiveTranscript(t);
    };
    recognition.onend = () => { setIsListening(false); recognitionRef.current = null; };
    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        setIsListening(false);
        if (event.error === "not-allowed") toast.error("Microphone access denied");
      }
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
  }, []);

  // Auto-send when listening stops and we have transcript
  useEffect(() => {
    if (!isListening && liveTranscript.trim() && !sending) {
      sendMessage(liveTranscript);
    }
  }, [isListening, liveTranscript, sending, sendMessage]);

  /* ─── Text to Speech ─── */
  const speakText = useCallback(async (text) => {
    // Try ElevenLabs first if assistant has a custom voice
    const voiceId = assistant?.elevenlabs_voice_id;
    if (voiceId && voiceId !== "browser_male" && voiceId !== "browser_female" && voiceId !== "browser_neutral") {
      try {
        setIsSpeaking(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/voices/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("appmi_token")}` },
          body: JSON.stringify({ text: text.slice(0, 1000), voice_id: voiceId }),
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(url); };
          audio.onerror = () => { setIsSpeaking(false); URL.revokeObjectURL(url); };
          audio.play();
          return;
        }
      } catch {}
      setIsSpeaking(false);
    }

    // Fallback: browser TTS
    if (!synthRef.current) return;
    synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1; utterance.pitch = 1;
    const voices = synthRef.current.getVoices();
    const gender = assistant?.voice_gender || "neutral";
    if (gender !== "neutral" && voices.length > 0) {
      const keywords = gender === "female"
        ? ["female", "woman", "samantha", "victoria", "karen", "fiona"]
        : ["male", "man", "daniel", "james", "alex", "david"];
      const match = voices.find((v) => keywords.some((k) => v.name.toLowerCase().includes(k)));
      if (match) utterance.voice = match;
    }
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
  }, [assistant]);

  const stopSpeaking = useCallback(() => {
    if (synthRef.current) synthRef.current.cancel();
    setIsSpeaking(false);
  }, []);

  /* ─── Copy Message ─── */
  const copyMessage = (msg) => {
    navigator.clipboard.writeText(msg.content).then(() => {
      setCopiedId(msg.id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  /* ─── Handle Input ─── */
  const handleTextSubmit = (e) => { e.preventDefault(); sendMessage(inputText); };
  const handleMicToggle = () => { isListening ? stopListening() : startListening(); };
  const handleTextareaChange = (e) => {
    setInputText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(inputText); }
  };

  /* ═══════════════════════════════════════════════════════
     Render: Loading / Error / Public / Owner
     ═══════════════════════════════════════════════════════ */
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-500/10 via-violet-500/10 to-fuchsia-500/10 flex items-center justify-center" data-testid="chat-loading">
        <Sparkles className="w-8 h-8 text-cyan-400 animate-pulse" />
      </div>
    );
  }

  if (error || !assistant) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-500/10 via-violet-500/10 to-fuchsia-500/10 flex items-center justify-center px-6 pt-16" data-testid="chat-error">
        <div className="text-center animate-fade-in-up">
          <h1 className="font-outfit text-2xl text-foreground mb-2">Assistant Not Found</h1>
          <p className="text-sm text-muted-foreground mb-6">This assistant doesn't exist or the URL is incorrect.</p>
          <Button onClick={() => navigate("/")} variant="outline" className="border-border text-foreground/80 hover:bg-white/5 bg-card rounded-lg press-down">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-500/10 via-violet-500/10 to-fuchsia-500/10 flex items-center justify-center px-6 pt-16" data-testid="chat-public-view">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative text-center max-w-md animate-fade-in-up">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-2xl font-medium shadow-lg">
            {assistant.name?.charAt(0).toUpperCase()}
          </div>
          <h1 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium mb-2 text-foreground" data-testid="public-assistant-name">
            {assistant.name}
          </h1>
          <p className="text-sm text-muted-foreground mb-1 capitalize">{assistant.purpose?.replace(/_/g, " ")} Assistant</p>
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className={`text-[10px] px-2.5 py-0.5 rounded-full border ${MODEL_BADGE_COLORS[normalizeModelId(assistant.ai_model)] || "bg-muted/40 text-muted-foreground border-border"}`}>
              {getModelName(assistant.ai_model)}
            </span>
            <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-muted/40 text-muted-foreground capitalize border border-border">
              {assistant.personality_tone}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-8">This is a personalized AI assistant. Log in to start chatting.</p>
          <Button data-testid="public-login-btn" onClick={() => navigate("/login", { state: { from: `/${slug}` } })} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white px-6 rounded-lg shadow-glow-sm press-down">
            <LogIn className="w-4 h-4 mr-2" /> Login to Chat
          </Button>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════
     Owner: Unified Chat Interface
     ═══════════════════════════════════════════════════════ */
  const aiInitial = assistant.name?.charAt(0).toUpperCase() || "A";

  return (
    <div className="h-screen flex overflow-hidden" data-testid="chat-page">
      {/* ─── Mobile Sidebar Backdrop ─── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
      )}

      {/* ─── Sidebar ─── */}
      <aside
        className={`fixed lg:relative z-40 lg:z-auto w-72 h-full bg-card/70 backdrop-blur-xl border-r border-white/[0.07] flex flex-col transform transition-transform duration-200 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="chat-sidebar"
      >
        {/* Sidebar Header */}
        <div className="flex-shrink-0 p-4 border-b border-border/60">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-sm font-medium">
                {aiInitial}
              </div>
              <div>
                <h2 className="font-outfit text-sm font-medium text-foreground leading-tight">{assistant.name}</h2>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${MODEL_BADGE_COLORS[normalizeModelId(assistant.ai_model)] || "bg-muted/40 text-muted-foreground border-border"}`}>
                  {getModelName(assistant.ai_model)}
                </span>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer" data-testid="close-sidebar-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <button
            onClick={newConversation}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs font-medium border-0 cursor-pointer hover:-translate-y-0.5 transition-all shadow-sm press-down"
            data-testid="new-conversation-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New Conversation
          </button>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2" data-testid="conversation-list">
          {conversations.length === 0 ? (
            <div className="text-center py-10">
              <MessageSquare className="w-6 h-6 text-muted-foreground/60 mx-auto mb-2" />
              <p className="text-[11px] text-muted-foreground/70">No conversations yet</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <ConvItem
                key={conv.id}
                conv={conv}
                active={conv.id === activeConvId}
                onClick={() => selectConversation(conv.id)}
                aiName={assistant.name}
              />
            ))
          )}
        </div>
      </aside>

      {/* ─── Main Chat Area ─── */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="flex-shrink-0 border-b border-white/10 bg-gradient-to-r from-cyan-600/20 via-violet-600/25 to-indigo-700/20 backdrop-blur-xl z-10">
          <div className="max-w-3xl mx-auto w-full px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white/70 hover:text-white bg-transparent border-0 cursor-pointer" data-testid="open-sidebar-btn">
                <PanelLeftOpen className="w-5 h-5" />
              </button>
              <button onClick={() => navigate("/feed")} className="hidden lg:block text-white/60 hover:text-white transition-colors bg-transparent border-0 cursor-pointer" data-testid="chat-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-medium">
                  {aiInitial}
                </div>
                <div>
                  <h1 className="font-outfit text-sm font-medium leading-tight text-white" data-testid="chat-assistant-name">{assistant.name}</h1>
                  <div className="flex items-center gap-1.5">
                    <Cpu className="w-3 h-3 text-cyan-200" />
                    <span className="text-[10px] text-cyan-200">{getModelName(assistant.ai_model)}</span>
                    <span className="text-[10px] text-cyan-300">|</span>
                    <Lock className="w-3 h-3 text-emerald-300" />
                    <span className="text-[10px] text-emerald-300" title="End-to-end encrypted">Encrypted</span>
                    {isSpeaking && <span className="text-[10px] text-cyan-400 font-medium animate-pulse">Speaking...</span>}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { ttsEnabled ? stopSpeaking() : null; setTtsEnabled(!ttsEnabled); }}
                className={`p-2 rounded-lg transition-all bg-transparent border-0 cursor-pointer ${ttsEnabled ? "text-white bg-white/20" : "text-white/50 hover:text-white hover:bg-white/10"}`}
                data-testid="chat-tts-toggle"
                title={ttsEnabled ? "Voice on" : "Voice off"}
              >
                {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto" data-testid="messages-area">
          <div className="max-w-3xl mx-auto w-full px-4 py-6 space-y-5">
            {messages.length === 0 && !isListening && (
              <div className="text-center py-20 animate-fade-in-up" data-testid="chat-empty-state">
                <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-cyan-500/15 to-violet-500/20 border border-cyan-500/30 flex items-center justify-center">
                  <span className="text-2xl font-outfit font-bold text-cyan-400">{aiInitial}</span>
                </div>
                <h2 className="font-outfit text-lg font-medium text-foreground mb-1">{assistant.name}</h2>
                <p className="text-sm text-muted-foreground mb-1 capitalize">{assistant.purpose?.replace(/_/g, " ")} Companion</p>
                <p className="text-xs text-muted-foreground/70 max-w-xs mx-auto">Start a conversation with your companion — type a message or tap the microphone.</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <MessageBubble key={msg.id || i} msg={msg} aiName={assistant.name} onCopy={copyMessage} copiedId={copiedId} />
            ))}

            {/* Live Transcription Bubble */}
            {isListening && liveTranscript && (
              <div className="flex gap-3 flex-row-reverse animate-message-in">
                <div className="max-w-[75%]">
                  <div className="flex items-center gap-2 mb-1 justify-end">
                    <span className="text-[11px] font-medium text-muted-foreground">You</span>
                    <span className="text-[10px] text-blue-500">Recording...</span>
                  </div>
                  <div className="bg-cyan-500/15 text-cyan-300 rounded-2xl rounded-br-md px-4 py-3 opacity-80">
                    <p className="text-sm leading-relaxed italic">{liveTranscript}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Typing Indicator */}
            {sending && (
              <div className="flex gap-3 animate-message-in" data-testid="typing-indicator">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-xs font-medium">
                  {aiInitial}
                </div>
                <div>
                  <span className="text-[11px] font-medium text-cyan-400 mb-1 block">{assistant.name}</span>
                  <div className="bg-card border border-border/60 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Voice Indicator Bar (when listening) */}
        {isListening && (
          <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-blue-500/10 backdrop-blur-sm border-t border-blue-500/20" data-testid="voice-indicator">
            <div className="flex items-end gap-[2px] h-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="w-[3px] bg-blue-500 rounded-full animate-bounce" style={{ height: `${8 + (i % 3) * 4}px`, animationDelay: `${i * 100}ms`, animationDuration: "0.6s" }} />
              ))}
            </div>
            <span className="text-xs text-blue-400 font-medium">Listening...</span>
            {liveTranscript && <span className="text-xs text-muted-foreground italic truncate flex-1">"{liveTranscript}"</span>}
            <button onClick={stopListening} className="text-xs text-red-500 hover:text-red-400 font-medium bg-transparent border-0 cursor-pointer press-down" data-testid="stop-listening-btn">
              Stop
            </button>
          </div>
        )}

        {/* Input Bar */}
        <div className="flex-shrink-0 border-t border-white/10 bg-background/70 backdrop-blur-xl" data-testid="chat-input-bar">
          <form onSubmit={handleTextSubmit} className="max-w-3xl mx-auto w-full px-4 py-3 flex items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid="chat-text-input"
              value={inputText}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${assistant.name}...`}
              disabled={sending || isListening}
              className="auto-resize-textarea flex-1 bg-white/[0.04] border border-white/10 text-foreground placeholder:text-muted-foreground/70 rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400/50 focus:shadow-glow-sm disabled:opacity-50"
              rows={1}
            />
            {/* Mic Button */}
            {speechSupported && (
              <button
                type="button"
                onClick={handleMicToggle}
                disabled={sending}
                className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border-0 cursor-pointer transition-all ${
                  isListening
                    ? "bg-red-500/15 text-red-500 shadow-[0_0_20px_rgba(239,68,68,0.15)]"
                    : "bg-muted/70 text-muted-foreground hover:bg-cyan-500/10 hover:text-cyan-400"
                } disabled:opacity-40`}
                data-testid="mic-button"
                title={isListening ? "Stop recording" : "Start recording"}
              >
                {isListening ? <MicOff className="w-4.5 h-4.5" /> : <Mic className="w-4.5 h-4.5" />}
              </button>
            )}
            {/* Send Button */}
            <Button
              data-testid="chat-send-btn"
              type="submit"
              disabled={(!inputText.trim() && !isListening) || sending}
              className="flex-shrink-0 h-11 w-11 p-0 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-400 hover:to-violet-600 text-white rounded-xl shadow-glow-sm disabled:opacity-30 disabled:shadow-none press-down"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
